import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

export const useStyles = makeStyles(({
  breakpoints,
  fontFamilies,
  fontSizes,
  fontWeights,
  siteMinWidth,
  borderRadiuses,
  palette,
}: Theme) => ({
  root: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: siteMinWidth,
    margin: '0 auto',
    filter: 'brightness(1)', // container for a child with position 'fixed'
    [breakpoints.up('sm')]: {
      flexDirection: 'column-reverse',
      marginBottom: 0,
      zIndex: 'unset',
    },
    [breakpoints.up('xl')]: {
      flexDirection: 'row',
    },
  },
  fullHeight: {
    minHeight: 'calc(100vh - 5rem)',
    '& > div:first-of-type': {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    },
  },
  titleBar: {
    position: 'relative',
    display: 'flex',
    gap: '0.375rem',
    '& > button': {
      position: 'absolute',
      left: 0,
    },
    [breakpoints.up('sm')]: {
      flexDirection: 'column',
      '& > button': {
        position: 'static',
      },
    },
  },
  ecpLayerRoot: {
    paddingTop: 24,
    [breakpoints.up('sm')]: {
      padding: '3rem 0 0',
    },
    [breakpoints.up('xl')]: {
      width: '38.25rem',
    },
  },
  ecpLayerRootCard: {
    position: 'relative',
    zIndex: 1,
    borderRadius: '16px 16px 0 0',
    background: palette.common.white,
    [breakpoints.up('xl')]: {
      marginTop: 0,
      borderRadius: borderRadiuses.xs,
      background: 'transparent',
    },
  },
  ecpLayerWrapper: {
    padding: '0 16px',
    [breakpoints.up('sm')]: {
      padding: '0 3rem',
    },
  },
  mapLayer: {
    position: 'relative',
    height: 'calc(100vh - 219px)',
    width: '100%',
    marginBottom: -64,
    [breakpoints.up('sm')]: {
      height: '55vh',
      display: 'flex',
      marginBottom: 0,
    },
    [breakpoints.up('xl')]: {
      position: 'sticky',
      top: '5rem',
      width: 'calc(100% - 38.25rem)',
      flexGrow: 1,
      height: 'calc(100vh - 80px)',
    },
  },
  mapLayerLong: {
    [breakpoints.down('sm')]: {
      height: 'calc(100vh - 265px)',
    },
  },
  mapLayerHidden: {
    display: 'none',
  },
  mapInactive: {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
  },
  searchBar: {
    display: 'flex',
    gap: '0.5rem',
    paddingBottom: 12,
    [breakpoints.up('sm')]: {
      paddingBottom: 24,
    },
    '& form': {
      flexGrow: 1,
      lineHeight: 1.43,
    },
    '& .MuiInputBase-root::before': {
      borderWidth: '2px',
      borderColor: palette.grey['400'],
    },
  },
  filterButtonDesktop: {
    display: 'none',
    [breakpoints.up('sm')]: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      width: 'fit-content',
      height: '100%',
      borderRadius: borderRadiuses.xs,
      boxShadow: 'none',
      backgroundColor: 'transparent',
    },
  },
  sortButtons: {
    width: '100%',
    [breakpoints.up('sm')]: {
      width: 330,
    },
  },
  storesList: {
    [breakpoints.up('sm')]: {
      margin: '-12px 0 0',
      paddingBottom: '1.75rem',
      bottom: 0,
    },
    [breakpoints.up('md')]: {
      minHeight: 'unset',
      margin: 0,
      paddingBottom: '2.25rem',
    },
  },
  mobileButtonsBar: {
    position: 'sticky',
    bottom: '4rem',
    left: 0,
    right: 0,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 0,
    marginBottom: '4rem',
    padding: '0 1rem',
    [breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  listButtons: {
    bottom: '3rem',
    marginBottom: '3rem',
  },
  mobileButtonsCenter: {
    position: 'relative',
    left: '50%',
    transform: 'translateX(-50%)',
    '& .MuiButton-root': {
      minHeight: '3rem',
      padding: '0.625rem 1rem',
      borderRadius: '1.5rem',
      lineHeight: 1.2,
      fontWeight: fontWeights.medium,
    },
  },
  mobileViewButton: {
    position: 'relative',
    width: 'fit-content',
    right: 16,
    marginLeft: 'auto',
    zIndex: 10,
  },
  mobileViewButtonStyle: {
    background: palette.common.white,
    border: 'none',
    borderRadius: '1.5rem',
    padding: '0.75rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  inputRoot: {
    minWidth: 'unset',
    '&.MuiInput-underline:after': {
      borderBottomColor: palette.primary.main,
    },
    '&.MuiOutlinedInput-root': {
      borderRadius: borderRadiuses.md,
      padding: '0 0 0 16px',
      '& fieldset': {
        borderColor: palette.grey['500'],
      },
      '&.Mui-focused fieldset': {
        borderColor: palette.primary.main,
      },
    },
  },
  input: {
    boxSizing: 'border-box',
    height: 43,
    color: palette.text.primary,
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.sm,
    padding: '12px 0',
    lineHeight: 1,
  },
  appointmentWrapper: {
    position: 'absolute',
    top: '1rem',
    left: '1rem',
    width: 'calc(100% - 32px)',
    [breakpoints.up('md')]: {
      top: '1.5rem',
      left: '1.5rem',
      width: 'calc(100% - 3rem)',
    },
  },
  toggle: {
    width: '13.75rem',
    marginBottom: '0.75rem',
    [breakpoints.up('sm')]: {
      marginBottom: '1.5rem',
    },
    '& button': {
      margin: 0,
    },
  },
}));
